import React from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { Flame, Crown, Search } from 'lucide-react'
import {
  AnimatedSection,
  StaggerItem,
  fadeInUp,
  staggerContainer,
  smoothEase,
} from './animations/AnimatedSection'

const cardVariants = {
  hidden: { opacity: 0, y: 60, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: smoothEase },
  },
}

const HowItWorksSection = () => {
  return (
    <Section>
      <HeaderContainer>
        <AnimatedSection variants={fadeInUp}>
          <SectionLabel>HOW IT WORKS</SectionLabel>
        </AnimatedSection>
        <AnimatedSection variants={fadeInUp} delay={0.1}>
          <SectionTitle>
            Three Ways to Make an <HighlightGreen>Impact</HighlightGreen>
          </SectionTitle>
        </AnimatedSection>
        <AnimatedSection variants={fadeInUp} delay={0.2}>
          <SectionSubtitle>
            Discover how our platform turns knowledge into real-world
            sustainable action
          </SectionSubtitle>
        </AnimatedSection>
      </HeaderContainer>

      <GridWrapper
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {/* Card 1 */}
        <StaggerItem variants={cardVariants}>
          <Card whileHover={{ y: -8, scale: 1.02 }}>
            <IconContainer>
              <Flame size={24} color="#2A2A2A" />
            </IconContainer>
            <CardTextContainer>
              <CardTitle>Take Lessons & Challenges</CardTitle>
              <CardDescription>
                Learn about global challenges, sustainability, and social impact
                topics through interactive lessons. Take quizzes to test your
                knowledge and earn points!
              </CardDescription>
            </CardTextContainer>
          </Card>
        </StaggerItem>

        {/* Card 2 */}
        <StaggerItem variants={cardVariants}>
          <Card whileHover={{ y: -8, scale: 1.02 }}>
            <IconContainer>
              <Crown size={24} color="#2A2A2A" />
            </IconContainer>
            <CardTextContainer>
              <CardTitle>Complete Action Paths</CardTitle>
              <CardDescription>
                Follow action paths with real challenges. Upload photos and
                videos of your completed actions to prove your impact and
                inspire others.
              </CardDescription>
            </CardTextContainer>
          </Card>
        </StaggerItem>

        {/* Card 3 */}
        <StaggerItem variants={cardVariants}>
          <Card whileHover={{ y: -8, scale: 1.02 }}>
            <IconContainer>
              <Search size={24} color="#2A2A2A" />
            </IconContainer>
            <CardTextContainer>
              <CardTitle>Research AI Search</CardTitle>
              <CardDescription>
                Access our AI-powered research library. Search thousands of
                studies, upload your own research, and contribute to global
                sustainability knowledge.
              </CardDescription>
            </CardTextContainer>
          </Card>
        </StaggerItem>
      </GridWrapper>
    </Section>
  )
}

const GridWrapper = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  width: 100%;
  max-width: 1200px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`

const Section = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 80px 24px;
  gap: 64px;
  width: 100%;

  /* Texture and dark green background */
  background-color: #1b3c19;
  background-image: url('https://www.transparenttextures.com/patterns/cream-paper.webp');
  background-blend-mode: multiply;
  @media (max-width: 768px) {
    padding: 60px 24px 48px;
  }
`

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 800px;
  width: 100%;
`

const SectionLabel = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;
  text-align: center;
  letter-spacing: -0.04em;
  text-transform: uppercase;
  color: #bbe148;
  margin: 0;
`

const SectionTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 56px;
  line-height: 64px;
  text-align: center;
  letter-spacing: -0.04em;
  color: #ffffff;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 40px;
    line-height: 48px;
  }
`

const HighlightGreen = styled.span`
  background: linear-gradient(90deg, #0e6409 0%, #bbe148 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
`

const SectionSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  text-align: center;
  font-size: 20px;
  padding: 0;
  line-height: 28px;
  color: #ffffff;
  margin: 0;
  width: 100%;
  max-width: 800px;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 24px;
  }
`

const Card = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 32px;
  flex: 1;
  max-width: 400px;
  min-width: 280px;
  min-height: 252px;

  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.15);
  border-radius: 16px;
  box-sizing: border-box;
  gap: 40px;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 48px 0 rgba(0, 0, 0, 0.25);
  }

  @media (max-width: 900px) {
    width: 100%;
    max-width: 100%;
  }
`

const IconContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  background-color: #bbe148;
  background-image: url('https://www.transparenttextures.com/patterns/cream-paper.webp');
  background-blend-mode: multiply;
  border-radius: 16px;
  flex-shrink: 0;
`

const CardTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const CardTitle = styled.h4`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #ffffff;
  margin: 0;
`

const CardDescription = styled.p`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  color: #ffffff;
  margin: 0;
`

export default HowItWorksSection
